// src/services/adminAPI.js
import axios from 'axios';

const API_URL = '/api';
// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const transactionToken = localStorage.getItem('transactionToken');
        if (transactionToken) {
            config.headers['X-Transaction-Token'] = transactionToken;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);


// Response interceptor
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('transactionToken');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

const adminAPI = {
    // Dashboard Stats
    getAdminStats: async (params) => {
        const response = await api.get('/admin/stats', { params });
        return response.data;
    },

    // User Management
    getAllUsers: async () => {
        const response = await api.get('/admin/users');
        return response.data;
    },

    getUserDetails: async (userId) => {
        const response = await api.get(`/admin/users/${userId}`);
        return response.data;
    },

    createUser: async (userData) => {
        const response = await api.post('/admin/users', userData);
        return response.data;
    },
    deleteUnsettledBets: async (marketId) => {
        const response = await api.delete(`/admin/bets/${marketId}`);
        return response.data;
    },

    updateUser: async (userId, userData) => {
        const response = await api.put(`/admin/users/${userId}`, userData);
        return response.data;
    },

    deleteUser: async (userId) => {
        const response = await api.delete(`/admin/users/${userId}`);
        return response.data;
    },

    toggleUserStatus: async (userId) => {
        const response = await api.patch(`/admin/users/${userId}/toggle-status`);
        return response.data;
    },

    toggleBettingStatus: async (userId) => {
        const response = await api.patch(`/admin/users/${userId}/toggle-betting`);
        return response.data;
    },

    updateExposureLimit: async (userId, limit) => {
        const response = await api.patch(`/admin/users/${userId}/exposure-limit`, { limit });
        return response.data;
    },

    changeUserPassword: async (userId, newPassword) => {
        const response = await api.patch(`/admin/users/${userId}/change-password`, { password: newPassword });
        return response.data;
    },

    // Wallet Management
    creditUser: async (userId, amount, description) => {
        const response = await api.post('/admin/wallet/credit', { userId, amount, description });
        return response.data;
    },

    debitUser: async (userId, amount, description) => {
        const response = await api.post('/admin/wallet/debit', { userId, amount, description });
        return response.data;
    },

    // Transactions
    getAdminTransactions: async (params) => {
        const response = await api.get('/admin/transactions', { params });
        return response.data;
    },

    getTransactionDetails: async (transactionId) => {
        const response = await api.get(`/admin/transactions/${transactionId}`);
        return response.data;
    },

    // Account Statement (NEW)
    getAccountStatement: async (params) => {
        const response = await api.get('/admin/account-statement', { params });
        return response.data;
    },

    // Bet Management
    getAllBets: async (params) => {
        const response = await api.get('/admin/bets', { params });
        return response.data;
    },

    getBetDetails: async (betId) => {
        const response = await api.get(`/admin/bets/${betId}`);
        return response.data;
    },

    settleBet: async (betId, result) => {
        const response = await api.post(`/admin/bets/${betId}/settle`, { result });
        return response.data;
    },
    settleBetManual: async (data) => {
        const response = await api.post('/admin/settle-bet', data);
        return response.data;
    },

    // Downline Management
    getDownline: async (userId, params) => {
        const response = await api.get(`/admin/downline/${userId}`, { params });
        return response.data;
    },

    getDownlineOverview: async (userId) => {
        const response = await api.get(`/admin/downline/${userId}/overview`);
        return response.data;
    },

    getDownlineBalances: async (userId) => {
        const response = await api.get(`/admin/downline/${userId}/balances`);
        return response.data;
    },

    // Recent Activities
    getRecentActivities: async (params) => {
        const response = await api.get('/admin/recent-activities', { params });
        return response.data;
    },

    // Reports
    generateReport: async (params) => {
        const response = await api.get('/admin/reports', { params });
        return response.data;
    },

    exportTransactions: async (params) => {
        const response = await api.get('/admin/transactions/export', { 
            params,
            responseType: 'blob' 
        });
        return response.data;
    },

    getMarketSelections: async (marketId) => {
        const response = await api.get(`/admin/settlement/${marketId}/selections`);
        return response.data;
    },

    settleBookmakerByWinner: async (data) => {
        const response = await api.post('/admin/settlement/bookmaker/settle', data);
        return response.data;
    },

    settleTiedMatch: async (data) => {
        const response = await api.post('/admin/settlement/tied/settle', data);
        return response.data;
    },

    settleSessionByRuns: async (data) => {
        const response = await api.post('/admin/settlement/session/settle', data);
        return response.data;
    },

    bulkSettleBySelection: async (data) => {
        const response = await api.post('/admin/settlement/bulk/selection', data);
        return response.data;
    },

    getUnsettledBookmakerBets: async (marketId) => {
        const response = await api.get(`/admin/settlement/bookmaker/${marketId}/unsettled`);
        return response.data;
    },

    getUnsettledTiedBets: async (marketId) => {
        const response = await api.get(`/admin/settlement/tied/${marketId}/unsettled`);
        return response.data;
    },

    getMarketsWithUnsettledBets: async () => {
    const response = await api.get('/admin/settlement/markets/unsettled');
    return response.data;
    },

    getUnsettledSessionBets: async (marketId) => {
        const response = await api.get(`/admin/settlement/session/${marketId}/unsettled`);
        return response.data;
    },
        unsettleBet: async (data) => {
        const response = await api.post('/admin/settlement/unsettle-bet', data);
        return response.data;
    },

    // Unsettle all bets for a market
    unsettleMarketBets: async (data) => {
        const response = await api.post('/admin/settlement/unsettle-market', data);
        return response.data;
    },

    // Unsettle bets by selection
    unsettleBySelection: async (data) => {
        const response = await api.post('/admin/settlement/unsettle-selection', data);
        return response.data;
    },

    // Get settled bets (for unsettle functionality)
    getSettledBets: async (marketId, params) => {
        const response = await api.get(`/admin/settlement/settled-bets/${marketId}`, { params });
        return response.data;
    },

    getLockedMatches: async () => {
        return await api.get('/admin/lock/locked-matches');
    },

    lockMatch: async (data) => {
        return await api.post('/admin/lock/lock-match', data);
    },

    unlockMatch: async (gmid) => {
        return await api.delete(`/admin/lock/unlock-match/${gmid}`);
    },

    checkMatchLock: async (gmid) => {
        return await api.get(`/admin/lock/check-lock/${gmid}`);
    }
    
    
};

export default adminAPI;